'use client';
import { useCallback, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from '@THC/hooks';
const transform = ({ current, previous, value }) => {
    if (current[0] !== current[1]) {
        return null;
    }
    if (typeof current[0] !== 'number') {
        return null;
    }
    if (typeof current[1] !== 'number') {
        return null;
    }
    const [start, end] = current;
    if (start > 0 && previous[0] === start && previous[1] === start + 1) {
        return [start - 1, end];
    }
    if (value[start]?.length) {
        return [start, end + 1];
    }
    return null;
};
const getSelectionState = (input) => [+input.selectionStart, +input.selectionEnd];
const ZERO = [0, 0];
const eq = (a, b) => a[0] === b[0] && a[1] === b[1];
const useCodeInputHandler = ({ inputRef, previousRef, setSelection, }) => useCallback(({ type }) => {
    const input = inputRef.current;
    const previous = previousRef.current;
    if (!previous || !input) {
        return;
    }
    const { selectionDirection: direction, value } = input;
    const current = getSelectionState(input);
    const save = (selection) => {
        if (eq(selection, previous)) {
            if (eq(selection, ZERO)) {
                return;
            }
            if (eq(selection, getSelectionState(input))) {
                return;
            }
        }
        previousRef.current = selection;
        setSelection((state) => (eq(state, selection) ? state : selection));
        input.setSelectionRange(...selection, direction || undefined);
    };
    if (type === 'selectionchange' && document.activeElement !== input) {
        return save([value.length, value.length]);
    }
    save(transform({ current, direction, previous, value }) || current);
}, [inputRef, previousRef, setSelection]);
const useCodeInputEffect = ({ handler, inputRef, previousRef, }) => {
    useIsomorphicLayoutEffect(() => {
        const input = inputRef.current;
        if (previousRef.current === undefined && input) {
            previousRef.current = getSelectionState(input);
        }
        const handlerRef = handler;
        input?.addEventListener('input', handlerRef);
        document.addEventListener('selectionchange', handlerRef, { passive: true });
        return () => {
            input?.removeEventListener('input', handlerRef);
            document.removeEventListener('selectionchange', handlerRef);
        };
    }, [inputRef, handler, previousRef]);
};
export const useCodeInput = (inputRef) => {
    const [selection, setSelection] = useState(ZERO);
    const previousRef = useRef();
    const handler = useCodeInputHandler({ inputRef, previousRef, setSelection });
    useCodeInputEffect({ handler, inputRef, previousRef });
    useIsomorphicLayoutEffect(() => {
        const handleReset = () => setSelection(ZERO);
        document.addEventListener('inputauthresetfocus', handleReset, { passive: true });
        return () => {
            document.removeEventListener('inputauthresetfocus', handleReset);
        };
    }, [inputRef]);
    return selection;
};
